/*!
 * Copyright © 2020-2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, pairwise, withLatestFrom } from 'rxjs/operators';

import { notEmptyRequiredValidator } from '../../../shared/validators';
import { EApiRequestStatus, IVerificationForm } from '../../models';
import { ResendVerificationCodeRequest, SetCredentialsRequest } from '../../store/actions';
import {
  getResendAttempts,
  getResendCodeRequestStatus,
  getUsername,
  getVerificationAttempts,
  getVerifyAccountRequestStatus
} from '../../store/selectors';
import { IAccountState } from '../../store/state';

@Component({
  selector: 'app-verify-your-account',
  templateUrl: './verify-your-account.component.html',
  styleUrls: ['./verify-your-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerifyYourAccountComponent {
  MAX_VERIFICATION_ATTEMPTS = 5;
  MAX_RESEND_ATTEMPTS = 4;

  form: FormGroup<IVerificationForm>;

  isTooManyResendAttempts$: Observable<boolean>;
  isTooManyVerificationAttempts$: Observable<boolean>;
  isMaxAttempts$: Observable<boolean>;
  isRequestFailure$: Observable<boolean>;
  username$: Observable<string>;
  resendCodeRequestStatus$: Observable<EApiRequestStatus>;
  verifyAccountRequestStatus$: Observable<EApiRequestStatus>;
  apiRequestStatus = EApiRequestStatus;

  verificationCode: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [notEmptyRequiredValidator, Validators.pattern('[0-9]{6}')]
  });

  constructor(private readonly store: Store<IAccountState>) {
    this.form = new FormGroup<IVerificationForm>({
      verificationCode: this.verificationCode
    });

    this.username$ = this.store.pipe(select(getUsername));
    this.resendCodeRequestStatus$ = this.store.pipe(select(getResendCodeRequestStatus));
    this.verifyAccountRequestStatus$ = this.store.pipe(select(getVerifyAccountRequestStatus));

    this.isTooManyResendAttempts$ = this.store
      .pipe(select(getResendAttempts))
      .pipe(map(resendAttempts => resendAttempts >= this.MAX_RESEND_ATTEMPTS));

    this.isTooManyVerificationAttempts$ = this.store
      .pipe(select(getVerificationAttempts))
      .pipe(map(verificationAttempts => verificationAttempts >= this.MAX_VERIFICATION_ATTEMPTS));

    this.isMaxAttempts$ = this.store
      .pipe(
        select(getVerificationAttempts),
        pairwise()
      )
      .pipe(
        map(([prev, current]) => prev < current),
        withLatestFrom(this.isTooManyResendAttempts$),
        map(([verificationAttemptsUpdated, resendAttempts]) => verificationAttemptsUpdated && resendAttempts)
      );

    this.isRequestFailure$ = combineLatest([this.resendCodeRequestStatus$, this.verifyAccountRequestStatus$]).pipe(
      map(requestStatus => requestStatus.includes(EApiRequestStatus.Failure))
    );
  }

  completeRequest(): void {
    this.store.dispatch(
      SetCredentialsRequest({
        verificationCode: this.verificationCode.value || ''
      })
    );
  }

  resendCode(): void {
    this.verificationCode.reset();
    this.store.dispatch(ResendVerificationCodeRequest());
  }
}
